import { useState, ReactElement, useContext } from 'react';
import classNames from 'classnames';
import { ChevronDown, Edit, Trash2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { AuthContext } from '@/context/auth-context';
import { CopyButton } from '@/components/ui/copy-button';
import { useLaunchDarkly } from '@/providers/launchdarkly-provider';

interface AccordionProps {
  id: string;
  isEdit?: boolean;
  isActive?: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  setIsEdit: (isEdit: boolean) => void;
  children: ReactElement;
  name: string;
  handleChangeName: (name: string) => void;
  handleSaveConfig: () => void;
  handleUpdateActive: () => void;
  setIsConfirmationModalOpen: (isOpen: boolean) => void;
  handleCancel?: () => void;
  handleUpdateOverride: () => void;
  isOverrideEnabled: boolean;
}

export const Accordion = ({
  id,
  children,
  name,
  isEdit,
  isActive,
  isLoading,
  isDisabled,
  isOverrideEnabled,
  handleUpdateOverride,
  handleChangeName,
  setIsEdit,
  handleSaveConfig,
  handleUpdateActive,
  setIsConfirmationModalOpen,
  handleCancel,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(!!isEdit);
  const { isAdmin } = useContext(AuthContext);
  const { isGuardrailActiveToggleEnabled } = useLaunchDarkly();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!name?.trim() || isDisabled) {
        return;
      }
      handleSaveConfig();
    }
  };

  return (
    <Card role='group' aria-label={name} id={`guardrail-${id}-container`}>
      <CardContent>
        <input
          id='expandCollapse'
          checked={isOpen}
          type='checkbox'
          className='peer sr-only'
          onChange={() => setIsOpen(!isOpen)}
        />
        <div className='w-full flex justify-between items-end transition-colors duration-500 ease-in-out'>
          {isEdit ? (
            <div className='mr-6 w-full'>
              <p className='text-md mb-2 font-semibold flex items-center gap-2'>
                <span>Name</span>
              </p>
              <Input
                id={`${id}-name`}
                placeholder='i.e. Ethical Appropriateness Check'
                value={name}
                onChange={(e) => {
                  handleChangeName(e.target.value);
                }}
                onKeyDown={handleKeyDown}
              />
            </div>
          ) : (
            <div
              className='font-semibold w-full cursor-pointer'
              onClick={() => setIsOpen(!isOpen)}
            >
              {name}
              {isAdmin && (
                <div
                  className='flex items-center gap-2 mt-2'
                  aria-hidden={true}
                >
                  <span
                    className='text-xs text-gray-500 dark:text-gray-400'
                    aria-hidden={true}
                  >
                    {id}
                  </span>
                  <CopyButton text={id} aria-label='Copy ID' />
                </div>
              )}
            </div>
          )}
          {isEdit ? (
            <div className='flex items-center gap-4 pl-4'>
              <Button
                variant='secondary'
                disabled={isLoading}
                onClick={() => {
                  if (handleCancel) {
                    handleCancel();
                  }
                  setIsEdit(false);
                }}
              >
                Cancel
              </Button>
              <Button
                id='primary-button'
                disabled={isDisabled}
                onClick={() => {
                  handleSaveConfig();
                }}
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner /> Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          ) : (
            <div className='flex items-center gap-5'>
              <div className='flex items-center space-x-2'>
                <Switch
                  disabled={!isActive}
                  id={`guardrail-${id}-override-switch`}
                  checked={isOverrideEnabled}
                  onCheckedChange={handleUpdateOverride}
                />
                <Label
                  htmlFor={`guardrail-${id}-override-switch`}
                  className='whitespace-nowrap'
                >
                  {isOverrideEnabled ? 'Override Enabled' : 'Override Disabled'}
                </Label>
              </div>
              {isGuardrailActiveToggleEnabled && (
                <div className='flex items-center space-x-2'>
                  <Switch
                    id={`guardrail-${id}-active-switch`}
                    checked={isActive}
                    onCheckedChange={handleUpdateActive}
                  />
                  <Label htmlFor={`guardrail-${id}-active-switch`}>
                    {isActive ? 'Active' : 'Inactive'}
                  </Label>
                </div>
              )}
              <Button
                id={`guardrail-${id}-edit`}
                variant='ghost'
                size='icon'
                onClick={() => {
                  setIsEdit(true);
                  setIsOpen(true);
                }}
              >
                <Edit className='h-4 w-4' />
              </Button>
              <Button
                id={`guardrail-${id}-delete`}
                variant='ghost'
                size='icon'
                onClick={() => {
                  setIsConfirmationModalOpen(true);
                }}
              >
                <Trash2 className='h-4 w-4' />
              </Button>
              <ChevronDown
                id={`guardrail-${id}-expand`}
                className={classNames('cursor-pointer h-4 w-4', {
                  'rotate-180': isOpen,
                })}
                onClick={() => setIsOpen(!isOpen)}
              />
            </div>
          )}
        </div>
        <div
          className={classNames(
            'overflow-hidden h-0 p-0',
            'peer-checked:h-fit',
          )}
          aria-hidden={!isOpen}
        >
          {isOpen && <Separator className='my-4' />}
          {isOpen && children}
        </div>
      </CardContent>
    </Card>
  );
};
